import { Controller } from "@hotwired/stimulus"
import { assessmentQuestionAnswered } from "../utils/assessment_answered"

// The CME Evaluation as a walkthrough (TMS-865): one screen at a time, a progress bar
// over the top, and a Submit that only appears on the last screen. Purely a matter of
// what is on screen — the whole form still posts once, exactly as the single scrolling
// page did, so nothing about what gets stored changes.
export default class extends Controller {
  static targets = [
    "screen",
    "progressLabel",
    "progressFill",
    "previousButton",
    "nextButton",
    "submitButton",
    "followUp"
  ]

  static values = { preview: Boolean }

  connect() {
    this.showScreen(0)
  }

  previousScreen(event) {
    event.preventDefault()
    this.step(this.currentIndex - 1)
  }

  // Moving on is never blocked — a learner may want to read ahead — but the screen
  // they leave marks anything unanswered on it, so they don't reach the end and find
  // Submit out of reach with no clue why.
  nextScreen(event) {
    event.preventDefault()
    this.currentScreen?.dispatchEvent(new CustomEvent("assessment:validate", { bubbles: true }))
    this.step(this.currentIndex + 1)
  }

  // Re-reads the form whenever an answer changes: reveals or hides the conditional
  // follow-ups, and decides whether the evaluation can be submitted yet.
  refresh() {
    this.syncFollowUps()
    this.syncSubmitButton()
  }

  // Opens the screen holding a question the validator flagged, so its error lands
  // somewhere the learner is actually looking.
  reveal(event) {
    const screen = this.screenTargets.find((candidate) => candidate.contains(event.target))
    if (screen) this.showScreen(this.indexOf(screen))
  }

  step(index) {
    if (index < 0 || index >= this.screenTargets.length) return

    this.showScreen(index)
    this.keepInView()
  }

  showScreen(index) {
    const total = this.screenTargets.length
    const onLastScreen = index === total - 1

    this.screenTargets.forEach((screen) => {
      screen.hidden = this.indexOf(screen) !== index
    })

    this.progressLabelTarget.textContent = `Question ${index + 1} of ${total}`
    this.progressFillTarget.style.width = `${((index + 1) / total) * 100}%`
    this.previousButtonTarget.disabled = index === 0
    // Submit takes Next's place only on the last screen, so arriving there reads as
    // the end of the evaluation rather than one more question.
    this.nextButtonTarget.hidden = onLastScreen
    this.submitButtonTarget.hidden = !onLastScreen

    this.refresh()
  }

  // A follow-up applies to one answer of the question above it, so only the learners
  // it applies to are shown it. Hiding it clears what they typed: an answer to a
  // question they are no longer being asked would otherwise be stored as if it stood.
  syncFollowUps() {
    this.followUpTargets.forEach((followUp) => {
      const triggered = this.followUpTriggered(followUp)
      followUp.hidden = !triggered
      if (triggered) return

      followUp.querySelectorAll("textarea").forEach((input) => {
        input.value = ""
      })
    })
  }

  followUpTriggered(followUp) {
    const screen = this.screenTargets.find((candidate) => candidate.contains(followUp))
    if (!screen) return false

    const triggerId = followUp.dataset.triggerOptionId
    return Array.from(screen.querySelectorAll(".academy-quiz-option-input"))
      .some((input) => input.value === triggerId && input.checked)
  }

  // Submit stays out of reach until the evaluation is complete, and stays there for
  // good in preview, where an author may try the answers but never send them. A
  // conditional follow-up is never counted: it carries no validator target, exactly
  // as the server never requires it.
  syncSubmitButton() {
    this.submitButtonTarget.disabled = this.previewValue || !this.allAnswered
  }

  // Screens differ in height, so stepping off a tall one can leave the learner staring
  // at the space under a short one. Only scrolled when the top of the form has actually
  // gone off screen, so a step between two short screens never jumps the page.
  keepInView() {
    if (this.element.getBoundingClientRect().top >= 0) return

    this.element.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  indexOf(screen) {
    return Number(screen.dataset.screenIndex)
  }

  get allAnswered() {
    return this.questionElements.every((question) => assessmentQuestionAnswered(question))
  }

  get questionElements() {
    return Array.from(this.element.querySelectorAll("[data-academy-assessment-target='question']"))
  }

  get currentScreen() {
    return this.screenTargets.find((screen) => !screen.hidden)
  }

  get currentIndex() {
    return this.currentScreen ? this.indexOf(this.currentScreen) : 0
  }
}
